import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserPositions } from '../hooks/useUserPositions';

export const MyPositions: React.FC = () => {
  const { user } = useAuth();
  const { positionGroups, loading, error, refreshing, refreshCurrentData } = useUserPositions(user?.walletAddress || null);

  // Auto-refresh state
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [autoRefreshMs, setAutoRefreshMs] = useState(120_000);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const id = setInterval(() => {
      if (!refreshing) {
        refreshCurrentData();
      }
    }, autoRefreshMs);

    return () => clearInterval(id);
  }, [autoRefreshEnabled, autoRefreshMs, refreshCurrentData, refreshing]);

  // Update lastUpdated when data loads
  useEffect(() => {
    if (!loading && !refreshing) {
      setLastUpdated(new Date());
    }
  }, [loading, refreshing]);

  if (loading) {
    return (
      <div className="my-positions">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your positions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-positions">
        <div className="error-container">
          <h2>Error Loading Positions</h2>
          <p>{error}</p>
          {!user?.walletAddress && (
            <p>
              <a href="/profile" className="link">Update your profile</a> to add a wallet address.
            </p>
          )}
          <button onClick={refreshCurrentData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const allPositions = positionGroups.flatMap(group => group.positions);
  const slipstreamPositions = positionGroups.find(g => g.type === 'slipstream')?.positions || [];
  const basicPositions = positionGroups.find(g => g.type === 'basic')?.positions || [];

  return (
    <div className="my-positions">
      <header className="positions-header">
        <div className="header-left">
          <h1>My Positions</h1>
          <p>This screen shows APRs and Claimable Yield for the deposits you have on Aerodrome.</p>
          {user?.walletAddress && (
            <p className="wallet-info">
              Wallet: {user.walletAddress.slice(0, 6)}...{user.walletAddress.slice(-4)}
            </p>
          )}
        </div>
        <div className="header-right">
          <div className="auto-refresh-controls">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={autoRefreshEnabled}
                onChange={e => setAutoRefreshEnabled(e.target.checked)}
              />
              <span>Auto Refresh</span>
            </label>
            <select
              value={autoRefreshMs}
              onChange={e => setAutoRefreshMs(Number(e.target.value))}
              disabled={!autoRefreshEnabled}
            >
              <option value={120000}>2 min</option>
            </select>
            {lastUpdated && (
              <span className="last-updated">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
          <button
            onClick={refreshCurrentData}
            className="refresh-button"
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <div className="spinner small"></div>
                Refreshing...
              </>
            ) : (
              'Refresh Positions'
            )}
          </button>
        </div>
      </header>

      {allPositions.length === 0 ? (
        <div className="empty-state">
          <h3>No Positions Found</h3>
          <p>Our service is either down for maintenance or you don't have any active positions at this time.</p>
        </div>
      ) : (
        <div className="positions-content">
          {slipstreamPositions.length > 0 && (
            <div className="positions-section">
              <h2>Slipstream Pools</h2>
              <div className="positions-table-container">
                <table className="positions-table">
                  <thead>
                    <tr>
                      <th>Pool Name</th>
                      <th>Pool Type</th>
                      <th>Range</th>
                      <th>ETH Price</th>
                      <th>In Range</th>
                      <th>Deposit</th>
                      <th>APR %</th>
                      <th>Claimable Yield</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slipstreamPositions.map((position, index) => (
                      <tr key={index}>
                        <td>
                          <div className="pool-name">
                            {position.poolName}
                          </div>
                        </td>
                        <td>
                          <span className="pool-type slipstream">
                            Slipstream
                          </span>
                        </td>
                        <td>
                          <span className="price-range">
                            {position.priceRange || 'Unknown'}
                          </span>
                        </td>
                        <td>
                          <span className="token-price">
                            ${position.tokenPrice ? position.tokenPrice.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'}
                          </span>
                        </td>
                        <td>
                          <span className={`in-range ${position.isInRange ? 'in-range-yes' : 'in-range-no'}`}>
                            {position.isInRange ? '✅' : '❌'}
                          </span>
                        </td>
                        <td>${position.positionValueUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <span
                              title="Work in Progress! This calculation is still experimental, but APR value should be in the ballpark."
                              style={{
                                cursor: 'help',
                                color: '#f59e0b',
                                fontSize: '14px'
                              }}
                            >
                              ⚠️
                            </span>
                            {position.aprCurrent.toFixed(2)}%
                          </span>
                        </td>
                        <td>
                          <span
                            title={`≈ $${(position.claimableEmissions * (position.aeroPrice || 0)).toFixed(2)} USD`}
                            style={{ cursor: 'help' }}
                          >
                            {position.claimableEmissions.toFixed(2)} AERO
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {basicPositions.length > 0 && (
            <div className="positions-section">
              <h2>Basic Pools</h2>
              <div className="positions-table-container">
                <table className="positions-table">
                  <thead>
                    <tr>
                      <th>Pool Name</th>
                      <th>Pool Type</th>
                      <th>Range</th>
                      <th>AERO Price</th>
                      <th>In Range</th>
                      <th>Deposit</th>
                      <th>APR %</th>
                      <th>Claimable Yield</th>
                    </tr>
                  </thead>
                  <tbody>
                    {basicPositions.map((position, index) => (
                      <tr key={index}>
                        <td>
                          <div className="pool-name">
                            {position.poolName}
                          </div>
                        </td>
                        <td>
                          <span className="pool-type basic">
                            Basic
                          </span>
                        </td>
                        <td>
                          <span className="price-range">
                            $0 to <span className="infinity-symbol">∞</span>
                          </span>
                        </td>
                        <td>
                          <span className="token-price">
                            ${position.tokenPrice ? position.tokenPrice.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : '0.0000'}
                          </span>
                        </td>
                        <td>
                          <span className="in-range in-range-yes">
                            ✅
                          </span>
                        </td>
                        <td>${position.positionValueUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td>{position.aprCurrent.toFixed(2)}%</td>
                        <td>
                          <span
                            title={`≈ $${(position.claimableEmissions * (position.aeroPrice || 0)).toFixed(2)} USD`}
                            style={{ cursor: 'help' }}
                          >
                            {position.claimableEmissions.toFixed(2)} AERO
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
